import { api } from '@goaltree/backend/convex/_generated/api'
import { cn } from '@goaltree/ui/lib/utils'
import { useConvexAuth, useQuery } from 'convex/react'
import { Lock } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { openHabitUpsell } from '@/lib/usage-limit'

// Free users' habit slots; `capped` once every free slot is taken.
export function useHabitCap() {
  const { isAuthenticated } = useConvexAuth()
  const allowance = useQuery(api.nodes.getMyHabitAllowance, isAuthenticated ? {} : 'skip')
  const limit = allowance?.limit ?? null
  const used = allowance?.used ?? 0
  return { used, limit, capped: limit !== null && used >= limit }
}

// "2 of 3 free habits" once a free user is one away from the cap; a locked row
// with Upgrade at the cap. Hidden on Pro / trial.
export function HabitCapHint({ className }: { className?: string }) {
  const { t } = useTranslation()
  const { used, limit, capped } = useHabitCap()
  if (limit === null || used < limit - 1) return null
  return (
    <div
      className={cn(
        'flex items-center gap-2 text-xs',
        capped
          ? 'rounded-xl border border-border/70 bg-muted/30 px-3 py-2 text-foreground'
          : 'px-1 text-muted-foreground',
        className,
      )}
    >
      {capped && <Lock className="size-3.5 shrink-0 text-muted-foreground" />}
      <span className="min-w-0 flex-1">
        {capped
          ? t('habitLimit.composerLocked', { limit })
          : t('habitLimit.composerHint', { used, limit })}
      </span>
      <button
        type="button"
        onClick={openHabitUpsell}
        className={cn(
          'shrink-0 cursor-pointer font-semibold text-primary-text transition-colors hover:underline',
          capped &&
            'rounded-full bg-primary px-2.5 py-1 text-primary-foreground hover:no-underline hover:bg-primary/90',
        )}
      >
        {t('habitLimit.upgrade')}
      </button>
    </div>
  )
}
